import { computed, watch, type MaybeRefOrGetter, toValue } from "vue";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/vue-query";
import { useApi, useToaster } from "@shopexperts/shared";
import type { AdminBannedEmailsListQueryParams, CreateBannedEmailRequest } from "@shopexperts/legacy-api";
import { BAN_LIST_QUERY_KEY } from "./useBanRules";

export const BANNED_EMAILS_QUERY_KEY = [...BAN_LIST_QUERY_KEY, "emails"] as const;

type BannedEmailsQuery = Omit<AdminBannedEmailsListQueryParams, "cursor">;

export function useBannedEmails(query: MaybeRefOrGetter<BannedEmailsQuery | undefined> = {}) {
    const api = useApi();
    const toaster = useToaster();

    const queryResult = useInfiniteQuery({
        queryKey: computed(() => [...BANNED_EMAILS_QUERY_KEY, "list", toValue(query) ?? {}]),
        queryFn: ({ pageParam }) => api.admin.banList.emails.list({
            query: toValue(query),
            cursor: pageParam,
        }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (lastPage) => lastPage.pagination.has_next
            ? lastPage.pagination.cursor ?? undefined
            : undefined,
        staleTime: 60_000,
        gcTime: 5 * 60_000,
    });

    watch(queryResult.error, (error) => {
        if (error) {
            toaster.create({ title: "Failed to load banned emails", type: "error" });
        }
    });

    return queryResult;
}

export function useBannedEmailMutations() {
    const api = useApi();
    const queryClient = useQueryClient();
    const toaster = useToaster();

    // Rule cards carry a banned_emails_count, so refresh the whole ban list.
    const invalidateBanList = () => {
        queryClient.invalidateQueries({ queryKey: [...BAN_LIST_QUERY_KEY] });
    };

    /** A manual ban always locks the account behind the address, if there is one. */
    const banEmail = useMutation({
        mutationFn: (body: CreateBannedEmailRequest) => api.admin.banList.emails.create({ body }),
        onSuccess: () => {
            toaster.create({ title: "Email banned", type: "success" });
            invalidateBanList();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to ban the email: ${error.message}`, type: "error" });
        },
    });

    /** Unban: drops the entry and restores the account's access to `regular`. */
    const unbanEmail = useMutation({
        mutationFn: (bannedEmailId: number | string) => (
            api.admin.banList.emails.delete({ params: { bannedEmailId } })
        ),
        onSuccess: () => {
            toaster.create({ title: "Email unbanned", type: "success" });
            invalidateBanList();
        },
        onError: (error: Error) => {
            toaster.create({ title: `Failed to unban the email: ${error.message}`, type: "error" });
        },
    });

    return {
        banEmail,
        unbanEmail,
    };
}
